// src/pages/Sortie.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { mklog, dbgFetch } from '../utils/debug';
import { useUser } from '../context/UserContext';
import toast from 'react-hot-toast';
import { STATUS_MAP } from '../constants/statusDefinitions';
import PageLayout from '../components/PageLayout';
import AdminSortiePanel from '../components/AdminSortiePanel';
import CotationOverlay from '../components/CotationOverlay';
import DeletePatientButton from '../components/DeletePatientButton';
import AddonsBadge from '../components/AddonsBadge';

import {
  RotateCcw,
  Check,
  Download,
  Smile,
  Meh,
  Angry,
  BadgeCheck,
} from 'lucide-react';

import { socket } from '../socket';
import { mapDbPatient } from '../utils/patientHelpers';
import { CONFIG } from '../config/config';
import { getDisplayMusiqId } from '../utils/musiqId';
import AdminRapportBiQuotidienPanel from '../components/AdminRapportBiQuotidienPanel';
import { createPortal } from 'react-dom';
import { listTypesSalles } from '../api/salles';

/* ---------------- helpers ---------------- */
const API_URL = (CONFIG.API_URL || '/api').replace(/\/+$/, '');


export default function Sortie({ patients = [], setPatients }) {
  const navigate = useNavigate();
  const log = mklog('SORTIE');
  const [local, setLocal] = useState([]);
  const [codesMap, setCodesMap] = useState({});
  const [commentsMap, setCommentsMap] = useState({});
  const [frontCotationMap, setFrontCotationMap] = useState({});
  const [exportsRefreshKey, setExportsRefreshKey] = useState(0);

  const [confirmId, setConfirmId] = useState(null);
  const [pendingPatient, setPendingPatient] = useState(null);

  const [rePatient, setRePatient] = useState(null);
  const [open, setOpen] = useState(false);
  const [reStep, setReStep] = useState(1);
  const [reSalle, setReSalle] = useState(null);

  const [overlayId, setOverlayId] = useState(null);
  const [sp, setSearchParams] = useSearchParams();

  const [exportDirHandle, setExportDirHandle] = useState(null);
  const fsSupported = typeof window !== 'undefined' && 'showDirectoryPicker' in window;
  const exportedIdsRef = useRef(new Set());

  const [confirmPatientOpen, setConfirmPatientOpen] = useState(false);
  const [patientMood, setPatientMood] = useState(2);
  const [sendSatisfactionEmail, setSendSatisfactionEmail] = useState(false);

  const { etablissement, user: me } = useUser();

  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  const [usersById, setUsersById] = useState({});
  const [typesSalles, setTypesSalles] = useState([]);

  useEffect(() => {
    listTypesSalles()
      .then(setTypesSalles)
      .catch(() => {});
  }, []);

  const getTypeSalleLibelle = (typeSalleId) => {
    if (!typeSalleId) return '-';
    const found = typesSalles.find((ts) => ts.id === typeSalleId || ts.libelle === typeSalleId);
    return found ? found.libelle : typeSalleId;
  };

  function userLabelFromObject(u) {
    return u?.displayName || [u?.prenom, u?.nom].filter(Boolean).join(' ') || u?.name || null;
  }

  async function fetchUserLabel(id) {
    if (!id || usersById[id]) return;
    try {
      const r = await fetch(`${API_URL}/users/${encodeURIComponent(id)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const u = await r.json();
      const label = userLabelFromObject(u) || id;
      setUsersById((m) => ({ ...m, [id]: { label } }));
    } catch {}
  }

  useEffect(() => {
    const refreshOnConnect = () => {
      try {
        socket.emit('getPatients');
      } catch {}
    };
    socket.on('connect', refreshOnConnect);
    refreshOnConnect();

    return () => {
      socket.off('connect', refreshOnConnect);
    };
  }, []);

  const getUid = (p) => getDisplayMusiqId(p);


  async function exportPatientPdfToLocal(patient) {
    try {
      const id = patient?.id;
      if (!id) throw new Error('Patient manquant');
      const provisional = patient?._provisional ? '1' : '0';

      const resp = await fetch(
        `${API_URL}/exports/patient/${id}/pdf?provisional=${provisional}&musiqId=${encodeURIComponent(getDisplayMusiqId(patient))}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error('HTTP ' + resp.status);

      const blob = await resp.blob();
      const ymd = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const base = `${ymd}_${getUid(patient) || id}_${(patient.nom || '').toUpperCase()}_${patient.prenom || ''}`.replace(/\s+/g, '-');
      const filename = `${base}.pdf`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF téléchargé.');
    } catch (e) {
      console.error('exportPatientPdfToLocal error', e);
      toast.error('Échec de génération du PDF.');
    }
  }

  async function handleProvisionalPdf(p) {
    try {
      await exportPatientPdfToLocal({ ...p, _provisional: true });
      setLocal((prev) =>
        prev.map((x) => (x.id === p.id ? { ...x, heure_sortie: new Date().toISOString() } : x)),
      );
    } catch {}
  }

  useEffect(() => {
    const cid = sp.get('cotation');
    setOverlayId(cid ?? null);
  }, [sp]);

  const stepOf = (p) => Number(p.currentStep ?? p.currentstep ?? p.current_step ?? NaN);
  const isArchived = (p) => stepOf(p) === 999 || /archiv/i.test(String(p.statut || ''));

  const occupiedBy = useMemo(() => {
    const map = new Map();
    patients.forEach((p) => {
      const step = Number(p.currentStep);
      if (p.salle && step >= 1 && step <= 6) {
        map.set(p.salle, `${p.prenom} ${p.nom?.toUpperCase?.()}`);
      }
    });
    return map;
  }, [patients]);

  const patientsStep7 = useMemo(
    () =>
      local.filter(
        (p) =>
          stepOf(p) === 7 &&
          !isArchived(p) &&
          p.etablissement_id === etablissement?.etablissement_id,
      ),
    [local, etablissement],
  );
  const patientsStep8 = useMemo(
    () =>
      local.filter(
        (p) =>
          stepOf(p) === 8 &&
          !isArchived(p) &&
          p.etablissement_id === etablissement?.etablissement_id,
      ),
    [local, etablissement],
  );

  useEffect(() => {
    const onList = (list) => {
      const arr = list.map(mapDbPatient);
      const filtered = arr.filter(
        (p) =>
          [7, 8].includes(stepOf(p)) &&
          !isArchived(p) &&
          p.etablissement_id === etablissement?.etablissement_id,
      );
      setLocal(filtered);
    };

    const onUpdate = (raw) => {
      const p = mapDbPatient(raw);
      const step = stepOf(p);

      setLocal((prev) => {
        const exists = prev.some((x) => x.id === p.id);
        const keep = [7, 8].includes(step) && !isArchived(p);
        if (keep) {
          const merge = (existing) => {
            if (!existing) return p;
            return {
              ...p,
              nom: p.nom || existing.nom,
              prenom: p.prenom || existing.prenom,
              civilite: p.civilite || existing.civilite,
              date_naissance: p.date_naissance || existing.date_naissance,
            };
          };
          return exists ? prev.map((x) => (x.id === p.id ? merge(x) : x)) : [p, ...prev];
        }
        return prev.filter((x) => x.id !== p.id);
      });

      let uBy = null;
      const c = p.commentaireMedecin ?? p.commentaire_medecin ?? undefined;
      if (c !== undefined) {
        const uAt = p.commentaireMedecinUpdatedAt ?? p.commentaire_medecin_updated_at ?? null;
        uBy = p.commentaireMedecinUpdatedBy ?? p.commentaire_medecin_updated_by ?? null;
        setCommentsMap((m) => ({
          ...m,
          [p.id]: { content: c || '', updatedAt: uAt, updatedBy: uBy },
        }));
      }
      if (uBy) fetchUserLabel(uBy);
    };

    socket.on('patientsList', onList);
    socket.on('patientUpdated', onUpdate);
    socket.emit('getPatients');

    return () => {
      socket.off('patientsList', onList);
      socket.off('patientUpdated', onUpdate);
    };
  }, [token, etablissement]);

  const upsertFromPatientFetch = useCallback(
    async (pid) => {
      if (!pid) return;
      try {
        const r = await fetch(`${API_URL}/passages/${pid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const raw = await r.json();
        const p = mapDbPatient(raw);

        if (p.codes) {
          setCodesMap((m) => ({ ...m, [p.id]: p.codes }));
        }

        try {
          const rc = await fetch(`${API_URL}/passages/${p.id}/commentaire`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (rc.ok) {
            const jc = await rc.json();
            setCommentsMap((m) => ({
              ...m,
              [p.id]: {
                content: jc?.content || '',
                updatedAt: jc?.updatedAt || null,
                updatedBy: jc?.updatedBy || null,
              },
            }));
            if (jc?.updatedBy) fetchUserLabel(jc.updatedBy);
          }
        } catch {}
      } catch {}
    },
    [API_URL, token],
  );

  useEffect(() => {
    function onFinalized(e) {
      const { id, codes, data } = e.detail || {};
      if (!id) return;
      setCodesMap((m) => ({ ...m, [id]: codes || '' }));
      setFrontCotationMap((m) => ({ ...m, [id]: data || {} }));
      setLocal((prev) =>
        prev.map((p) =>
          String(p.id) === String(id)
            ? { ...p, currentstep: 8, currentStep: 8, cotationStatus: 'complete' }
            : p,
        ),
      );
    }
    window.addEventListener('musiq:cotation:finalized', onFinalized);
    return () => window.removeEventListener('musiq:cotation:finalized', onFinalized);
  }, []);

  useEffect(() => {
    [...patientsStep7, ...patientsStep8].forEach((p) => {
      if (p && p.id && !codesMap[p.id]) upsertFromPatientFetch(p.id);
    });
  }, [patientsStep7, patientsStep8, token, codesMap, upsertFromPatientFetch]);

  const handleLibererClick = (p) => {
    log('click Archiver', { id: p?.id, salle: p?.salle });
    setPendingPatient(p);
    setConfirmId(p.id);
    setConfirmPatientOpen(true);
  };

  const cancelLiberer = () => {
    setConfirmId(null);
    setPendingPatient(null);
    setConfirmPatientOpen(false);
  };

  const confirmLiberer = async (forcedPatient = null) => {
    const p = forcedPatient || pendingPatient || local.find((x) => x.id === (confirmId || null));
    const id = p?.id;
    if (!id) return;
    if (p && stepOf(p) >= 8 && !exportedIdsRef.current.has(id)) {
      try {
        await exportPatientPdfToLocal(p);
        exportedIdsRef.current.add(id);
      } catch {}
    }
    const backup = [...local];
    setLocal((prev) => prev.filter((x) => x.id !== id));
    try {
      const now = new Date().toISOString();
      const res = await dbgFetch('SORTIE', `${API_URL}/passages/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          currentStep: 999,
          statut: 'Archive',
          salle: p?.salle || null,
          heure_sortie: now,
          mood: patientMood,
          send_satisfaction_email: sendSatisfactionEmail,
        }),
      });
      if (!res.ok) throw new Error(res.statusText);
      toast.success('Patient archivé');
      setTimeout(() => setExportsRefreshKey((k) => k + 1), 200);
    } catch (err) {
      console.error(err);
      log('Archiver ERROR', err?.message);
      toast.error('Impossible d’archiver');
      setLocal(backup);
    } finally {
      cancelLiberer();
    }
  };

  function getSalleFromId(id, salles) {
    return id == null ? null : (salles.find((s) => s.salle_id === id) ?? null);
  }

  const openReintegrate = (p) => {
    setRePatient(p);
    setReStep(Math.min(Math.max(stepOf(p) || 1, 1), 6));
    setReSalle(p?.salle_id ? getSalleFromId(p.salle_id, etablissement.salles) : null);
    setOpen(true);
  };

  async function handleConfirmReintegrate({ step, salle }) {
    if (!rePatient) return;
    const payload =
      step === 0
        ? { currentStep: 0, salle_id: null, cotationStatus: null, statut: '' }
        : { currentStep: step, salle_id: salle.salle_id, cotationStatus: null, statut: '' };
    try {
      const res = await fetch(`${API_URL}/passages/${rePatient.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw = await res.json();
      const p = mapDbPatient(raw);
      setPatients((prev) => prev.map((x) => (x.id === p.id ? p : x)));
      setLocal((prev) => prev.filter((x) => x.id !== p.id));
      toast.success('Patient réintégré');
      setOpen(false);
      setRePatient(null);
    } catch (e) {
      console.error(e);
      toast.error('Impossible de réintégrer');
    }
  }

  const handleOverlayClose = async () => {
    const id = overlayId;
    setSearchParams({});
    if (!id) return;
    await upsertFromPatientFetch(id);
    try {
      const res = await fetch(`${API_URL}/passages/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const raw = await res.json();
      const p = mapDbPatient(raw);
      setLocal((prev) => {
        const exists = prev.some((x) => x.id === p.id);
        const keep = [7, 8].includes(stepOf(p)) && !isArchived(p);
        if (keep) return exists ? prev.map((x) => (x.id === p.id ? p : x)) : [p, ...prev];
        return prev.filter((x) => x.id !== p.id);
      });
    } catch {}
  };

  const handleMoodChange = (mood) => {
    setPatientMood(mood);
    setSendSatisfactionEmail(mood === 1);
  };

  return (
    <>
      <PageLayout title="Liste sortie" sos={{ number: CONFIG.SOS_NUMBER }}>
        {confirmPatientOpen &&
          createPortal(
            <div className="fixed top-0 left-0 w-full h-[100vh] z-[1002] flex items-center justify-center bg-black/60">
              <div
                className="absolute inset-0 bg-black/30"
                onClick={() => setConfirmPatientOpen(false)}
              />
              <div className="relative z-10 mx-3 w-[80vw] md:w-[30vw] min-w-[330px] max-w-3xl rounded-2xl bg-white p-6 shadow-xl">
                <div className="mb-6">
                  <p className="mb-3 text-center text-sm font-medium text-gray-700">
                    Ce patient est-il satisfait ?
                  </p>

                  <div className="flex justify-center gap-3">
                    <button
                      type="button"
                      onClick={() => handleMoodChange(1)}
                      className={
                        patientMood === 1
                          ? 'flex h-20 w-20 items-center justify-center rounded-xl bg-green-500 text-white'
                          : 'flex h-20 w-20 items-center justify-center rounded-xl bg-gray-200 text-gray-400'
                      }
                    >
                      <Smile size={32} />
                    </button>

                    <button
                      type="button"
                      onClick={() => handleMoodChange(2)}
                      className={
                        patientMood === 2
                          ? 'flex h-20 w-20 items-center justify-center rounded-xl bg-yellow-500 text-white'
                          : 'flex h-20 w-20 items-center justify-center rounded-xl bg-gray-200 text-gray-400'
                      }
                    >
                      <Meh size={32} />
                    </button>

                    <button
                      type="button"
                      onClick={() => handleMoodChange(3)}
                      className={
                        patientMood === 3
                          ? 'flex h-20 w-20 items-center justify-center rounded-xl bg-red-500 text-white'
                          : 'flex h-20 w-20 items-center justify-center rounded-xl bg-gray-200 text-gray-400'
                      }
                    >
                      <Angry size={32} />
                    </button>
                  </div>
                </div>

                <div className="py-5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      Envoyer un email de satisfaction client ?
                    </span>

                    <button
                      type="button"
                      role="switch"
                      aria-checked={sendSatisfactionEmail}
                      onClick={() => setSendSatisfactionEmail((prev) => !prev)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        sendSatisfactionEmail ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                          sendSatisfactionEmail ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
                <div className="flex gap-3 pt-5 border-t border-gray-300">
                  <button
                    type="button"
                    onClick={() => confirmLiberer(pendingPatient)}
                    className="flex-1 rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white text-sm shadow-sm transition hover:bg-blue-700 active:scale-[0.99]"
                  >
                    Archiver
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmPatientOpen(false)}
                    className="flex-1 rounded-xl border border-gray-300 bg-white px-5 py-3 font-semibold text-gray-700 text-sm shadow-sm transition hover:bg-gray-50 active:scale-[0.99]"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )}

        {open &&
          createPortal(
            <div className="fixed top-0 left-0 w-full h-[100vh] z-[1002] flex items-center justify-center bg-black/60">
              <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
              <div className="relative z-10 grid min-h-[100svh] place-items-center p-3 sm:p-4">
                <div
                  className="w-[min(640px,100%)] max-h-[86vh] overflow-y-auto rounded-2xl bg-white shadow-2xl border border-slate-200"
                  style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
                >
                  <div className="px-5 pt-5 pb-3 border-b">
                    <h3 className="text-xl sm:text-2xl font-semibold tracking-tight">
                      Réaffecter le patient
                    </h3>
                    <p className="mt-1 text-sm text-slate-600">
                      Choisis l’étape et la salle. Tu peux confirmer ensuite.
                    </p>
                  </div>

                  <div className="px-5 py-4 space-y-6">
                    <div>
                      <div className="mb-2 text-sm font-medium text-slate-700">Étape</div>
                      <div className="flex flex-wrap gap-2">
                        {[0, 1, 2, 3, 4, 5, 6].map((s) => (
                          <button
                            key={s}
                            onClick={() => setReStep(s)}
                            className={[
                              'px-3 py-1.5 rounded-full text-sm border transition',
                              reStep === s
                                ? 'bg-red-600 border-red-600 text-white shadow'
                                : 'bg-white border-slate-300 text-slate-800 hover:bg-slate-50',
                            ].join(' ')}
                            title={s === 0 ? 'Accueil' : STATUS_MAP.get(s)?.name || `Étape ${s}`}
                          >
                            {s === 0 ? 'Accueil' : STATUS_MAP.get(s)?.name || `Étape ${s}`}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="mb-2 text-sm font-medium text-slate-700">Salle</div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => setReSalle(null)}
                          className={[
                            'px-3 py-1.5 rounded-lg text-sm border transition relative',
                            reSalle === null
                              ? 'bg-red-600 border-red-600 text-white shadow'
                              : 'bg-white border-slate-300 text-slate-800 hover:bg-slate-50',
                          ].join(' ')}
                        >
                          Aucune
                        </button>

                        {etablissement?.salles
                          ?.slice()
                          .sort((a, b) => a.salle_nom.localeCompare(b.salle_nom))
                          .map((s) => (
                            <button
                              key={s.salle_id}
                              onClick={() => setReSalle(s)}
                              className={[
                                'px-3 py-1.5 rounded-lg text-sm border transition relative',
                                reSalle?.salle_id === s.salle_id
                                  ? 'bg-red-600 border-red-600 text-white shadow'
                                  : 'bg-white border-slate-300 text-slate-800 hover:bg-slate-50',
                              ].join(' ')}
                              title={
                                occupiedBy.get(s.salle_id)
                                  ? `Occupée par ${occupiedBy.get(s.salle_id)}`
                                  : 'Libre'
                              }
                            >
                              {s.salle_nom}
                              {occupiedBy.get(s.salle_id) && (
                                <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-amber-500" />
                              )}
                            </button>
                          ))}
                      </div>

                      {occupiedBy.get(reSalle) && (
                        <div className="mt-2 text-xs text-amber-700">
                          Salle occupée par {occupiedBy.get(reSalle)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t px-5 py-3 flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setOpen(false);
                        setRePatient(null);
                      }}
                      className="px-3 py-2 rounded border border-slate-300 hover:bg-slate-50"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={() =>
                        handleConfirmReintegrate({ step: reStep, salle: reSalle || null })
                      }
                      className="px-3 py-2 rounded bg-red-600 text-white hover:bg-red-700"
                    >
                      Confirmer
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )}

        <main className="flex-grow overflow-auto px-4 py-6 pb-28 space-y-10 max-w-7xl mx-auto w-full">
          <section className="space-y-4 w-full">
            <h2 className="sr-only">En sortie</h2>
            {patientsStep7.length === 0 ? (
              <p className="italic text-gray-500">Aucun patient en sortie.</p>
            ) : (
              <div className="grid gap-3 sm:gap-4">{patientsStep7.map(renderPatientCard)}</div>
            )}
          </section>

          <section className="space-y-4 w-full">
            <h2 className="sr-only">Cotation effectuée</h2>
            {patientsStep8.length === 0 ? (
              <p className="italic text-gray-500">Aucun patient coté.</p>
            ) : (
              <div className="grid gap-3 sm:gap-4">{patientsStep8.map(renderPatientCard)}</div>
            )}
          </section>

          <section className="space-y-4 mt-8 w-full">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Sorties (période)</h2>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between rounded-t-xl">
                <p className="text-sm text-slate-600">
                  Exports et récapitulatif des sorties par période.
                </p>
                <span className="text-[11px] text-slate-400 uppercase tracking-wide">
                  Lecture seule
                </span>
              </div>

              <div className="px-3 sm:px-4 py-3">
                <AdminSortiePanel
                  refreshKey={exportsRefreshKey}
                  openReintegrate={openReintegrate}
                />
              </div>
            </div>
          </section>

          <section className="hidden lg:block space-y-4 mt-8 w-full">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Revue bi-quotidienne des facturations</h2>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
              <div className="px-3 sm:px-4 py-3">
                <AdminRapportBiQuotidienPanel etablissementId={etablissement?.etablissement_id} />
              </div>
            </div>
          </section>
        </main>
        
      </PageLayout>
    </>
  );

  function renderPatientCard(p) {
    const comment = commentsMap[p.id]?.content || '';
    const commentAt = commentsMap[p.id]?.updatedAt || null;
    const commentBy = commentsMap[p.id]?.updatedBy || null;

    const fmtDT = (raw) =>
      raw
        ? new Date(raw).toLocaleString('fr-FR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })
        : '';

    const actesVA = p.actes_valeur_ajoutee || p.actesValeurAjoutee || {};
    const hasActesVA = Object.values(actesVA).some((val) => val === true);
    const uid = getUid(p);

    return (
      <div
        key={p.id}
        className="relative border rounded-xl p-4 sm:p-5 bg-white shadow-sm transition hover:shadow-md cursor-pointer flex flex-col md:flex-row justify-between items-start md:items-center gap-4"
        onClick={() => navigate(`/sortie/${p.id}`)}
      >
        {/* Infos patient à gauche */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => openReintegrate(p)}
                className="h-7 w-7 sm:h-8 sm:w-8 inline-grid place-items-center rounded-md bg-red-600 text-white"
                title="Réintégrer"
                aria-label="Réintégrer"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleLibererClick(p)}
                className="h-7 w-7 sm:h-8 sm:w-8 inline-grid place-items-center rounded-md bg-emerald-600 text-white"
                title="Finaliser"
                aria-label="Finaliser"
              >
                <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </button>
              <button
                onClick={() => handleProvisionalPdf(p)}
                className="h-7 w-7 sm:h-8 sm:w-8 inline-grid place-items-center rounded-md bg-slate-800 text-white"
                title="Télécharger PDF (provisoire)"
                aria-label="Télécharger PDF (provisoire)"
              >
                <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </button>
            </div>
            <p className="text-slate-900 text-lg sm:text-xl font-semibold break-words ml-1">
              {p.civilite || ''} {p.prenom} {p.nom?.toUpperCase?.()}
            </p>
          </div>

          <p className="text-sm text-gray-600 mt-2">
            Né(e) le{' '}
            {p?.dateNaissance ? new Date(p.dateNaissance).toLocaleDateString('fr-FR') : '-'}
          </p>
          {uid ? <p className="text-xs font-mono text-gray-500">{uid}</p> : null}
          <p className="text-sm text-gray-600">Salle : {p.salle || '-'}</p>
          <p className="text-sm text-gray-600">
            Type de salle :{' '}
            <span className="font-medium text-slate-800">
              {getTypeSalleLibelle(p.type_salle || p.typeSalle)}
            </span>
          </p>

          {/* Notes */}
          {comment?.trim() && (
            <div className="mt-3 rounded-lg border border-red-100 bg-red-50/50 p-3">
              <div className="text-[11px] text-red-700 uppercase tracking-wide mb-1">Notes</div>
              <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">{comment}</div>
              {(commentAt || commentBy) && (
                <div className="mt-1 text-[11px] text-red-700/70">
                  {commentAt ? `Modifié le ${fmtDT(commentAt)}` : ''}
                  {commentAt && commentBy ? ' · ' : ''}
                  {commentBy ? `Auteur : ${usersById[commentBy]?.label || commentBy}` : ''}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Biologie & Actes VA à droite */}
        <div
          className="flex flex-col justify-between items-start md:items-end gap-3 shrink-0 w-full md:w-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <AddonsBadge bio={p.biologie} patient={p} />

          {hasActesVA && (
            <div className="rounded-lg border border-[#E1141C]/30 bg-white p-3">
              <div className="text-[11px] text-[#B3000A] font-semibold uppercase tracking-wide mb-1 flex items-center gap-1">
                <BadgeCheck className="h-3.5 w-3.5" /> Actes à valeur ajoutée
              </div>
              <div className="flex flex-wrap gap-1.5 justify-start md:justify-end">
                {Object.entries(actesVA)
                  .filter(([_, isChecked]) => isChecked)
                  .map(([key]) => (
                    <span
                      key={key}
                      className="px-2.5 py-1 bg-red-100 text-[#B3000A] text-xs font-semibold rounded-md border border-red-200"
                    >
                      {key}
                    </span>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}